import type { ArchitectureStatus } from '../dto/ArchitectureStatus';
import type { BackupRepository } from '../ports/BackupRepository';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import type { JournalRepository } from '../ports/JournalRepository';
import type { LocalDatabase } from '../ports/LocalDatabase';
import type { PhilosophyRepository } from '../ports/PhilosophyRepository';
import type { ProfileRepository } from '../ports/ProfileRepository';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import type { RoutineTemplateRepository } from '../ports/RoutineTemplateRepository';
import type { RoutineStepTemplateRepository } from '../ports/RoutineStepTemplateRepository';
import type { WorkoutRepository } from '../ports/WorkoutRepository';

export interface ArchitectureDependencies {
  readonly clock: Clock;
  readonly idGenerator: IdGenerator;
  readonly database: LocalDatabase;
  readonly profiles: ProfileRepository;
  readonly dailyRoutines: DailyRoutineRepository;
  readonly dailySteps: DailyStepRepository;
  readonly routineTemplates: RoutineTemplateRepository;
  readonly routineStepTemplates: RoutineStepTemplateRepository;
  readonly journals: JournalRepository;
  readonly workouts: WorkoutRepository;
  readonly philosophy: PhilosophyRepository;
  readonly backups: BackupRepository;
  readonly routineTransactions: RoutinePersistenceTransactions;
}

export async function getArchitectureStatus(
  dependencies: ArchitectureDependencies,
): Promise<readonly ArchitectureStatus[]> {
  const health = await dependencies.database.healthCheck().catch(() => ({
    storageSupported: false,
    adapterLoaded: true,
    schemaVersion: 0,
    healthy: false,
  }));

  return [
    { label: 'Domain loaded', available: true },
    { label: 'Application ports loaded', available: true },
    { label: 'Database adapter loaded', available: health.adapterLoaded },
    { label: 'Presentation layer loaded', available: true },
    { label: 'IndexedDB support detected', available: health.storageSupported },
    { label: `Schema version ${health.schemaVersion}`, available: true },
    { label: 'Persistence health check', available: health.healthy },
  ];
}
